import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';

// material-ui
import StyledEngineProvider from '@mui/material/StyledEngineProvider';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// project import
import useConfig from 'src/hooks/useConfig';
import Palette from './palette';
import Typography from './typography';
import CustomShadows from './shadows';
import componentsOverride from './overrides';

// ==============================|| DEFAULT THEME - MAIN ||============================== //

export default function ThemeCustomization({ children }) {
	const { themeDirection, mode, presetColor, fontFamily } = useConfig();

	const theme = useMemo(() => Palette(mode, presetColor), [mode, presetColor]);

	const themeTypography = useMemo(() => Typography(fontFamily), [fontFamily]);

	const themeCustomShadows = useMemo(() => CustomShadows(theme), [theme]);

	const themeOptions = useMemo(
		() => ({
			breakpoints: {
				values: {
					xs: 0,
					sm: 768,
					md: 1024,
					lg: 1266,
					xl: 1440,
				},
			},
			direction: themeDirection,
			mixins: {
				toolbar: {
					minHeight: 60,
					paddingTop: 8,
					paddingBottom: 8,
				},
			},
			palette: theme.palette,
			customShadows: themeCustomShadows,
			typography: themeTypography,
		}),
		[themeDirection, theme, themeTypography, themeCustomShadows],
	);

	const themes = createTheme(themeOptions);
	themes.components = componentsOverride(themes);

	const { isDev } = useConfig();

	useEffect(() => {
		if (isDev) {
			// eslint-disable-next-line no-console
			console.log('Current theme details:', themes);
		}
	}, [isDev, themes]);

	return (
		<StyledEngineProvider injectFirst>
			<ThemeProvider theme={themes}>
				<CssBaseline enableColorScheme />
				{children}
			</ThemeProvider>
		</StyledEngineProvider>
	);
}

ThemeCustomization.propTypes = { children: PropTypes.node };
